// Phase 9.4 — Master Data uploads page.
//
// This page is now for maintenance / bulk restatement only. The daily
// stock upload lives on the dashboard hero. Zones are ordered
// vendor-rates → sales → stock-balance to reflect the "how often do
// I actually touch this?" hierarchy.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

const ZONES = [
  {
    key: "vendor_rates",
    label: "Vendor Rates + Item Master (Excel)",
    accept: ".xlsx",
    endpoint: "/fnb/uploads/vendor-rates",
    description:
      "One row per (item, vendor). Item master fields (Reorder / Max / TAT / Danger Level) enrich existing items. Unmatched names get a placeholder SKU.",
    testid: "dropzone-vendor-rates",
  },
  {
    key: "sales",
    label: "Cafe Sales (Excel, streaming)",
    accept: ".xlsx",
    endpoint: "/fnb/uploads/sales",
    description:
      "Large POS export. Processed in 5,000-row chunks. Branch names are auto-mapped to canonical locations; unmapped branches are listed for admin review.",
    testid: "dropzone-sales",
  },
  {
    key: "stock_balance",
    label: "Stock Balance CSV (bulk restatement)",
    accept: ".csv",
    endpoint: "/fnb/uploads/stock-balance",
    description:
      "Wide-format inventory snapshot. Junk (Store-NA / Store-Delete) columns are dropped automatically. Negative qtys are preserved as reported.",
    testid: "dropzone-stock-balance",
    note: "For occasional bulk restatement — use the dashboard for daily stock uploads.",
  },
  {
    // Phase 9.6 — recipe / bill-of-materials master upload.
    key: "bom",
    label: "Bill of Materials (Recipe CSV)",
    accept: ".csv",
    endpoint: "/fnb/uploads/bom",
    description:
      "Recipe mapping from menu items → raw materials. Enables Sold 7d / 14d / 30d consumption to compute for cooked and mixed items. One row per (parent SKU, ingredient) pair.",
    testid: "dropzone-bom",
    note: "Triggers a background recompute of calculated levels + per-location activity after each successful upload.",
  },
  {
    // Phase 9.7 — file-driven ordering rules.
    key: "ordering_rules_link",
    label: "Ordering Rules (XLSX)",
    accept: ".xlsx",
    linkTo: "/tools/fnb-po/ordering-rules",
    description:
      "Per-item reorder levels + fixed purchase quantities. Powers the 'File rules' PO strategy in the auto-suggest wizard. Uploaded from the dedicated page — preview-then-commit + manual-lock protection.",
    testid: "dropzone-ordering-rules-link",
    note: "Opens the Ordering Rules master data page.",
  },
  {
    // Phase 9.7.9 — multidate activity ingest.
    key: "multidate_activity_link",
    label: "Multidate Activity Ingest (CSV × 3)",
    accept: ".csv",
    linkTo: "/tools/fnb-po/multidate-activity",
    description:
      "Upload 3 monthly POS activity CSVs. Aggregates the trio, flips per-outlet activity flags, and stores 90-day consumption rates. Two-step preview → commit with store mapping override.",
    testid: "dropzone-multidate-activity-link",
    note: "Opens the Multidate Activity Ingest page.",
  },
];

function bytesLabel(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ResultBlock({ result, meta, onForce }) {
  if (!result) return null;
  const dup = result.duplicate_upload;
  const rows = result.row_count ?? result.rates_upserted ?? result.items_upserted;

  return (
    <div className="mt-3 space-y-2" data-testid={`${meta.testid}-result`}>
      {dup ? (
        <Alert
          className="border-amber-300 bg-amber-50/60 text-amber-900"
          data-testid={`${meta.testid}-duplicate`}
        >
          <AlertTitle>Duplicate upload detected</AlertTitle>
          <AlertDescription className="space-y-2">
            <div className="text-xs">
              This exact file (SHA-256 <code className="font-mono">{result.source_sha256?.slice(0, 12)}…</code>)
              has already been ingested — no new rows written.
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={onForce}
              data-testid={`${meta.testid}-force-reimport`}
            >
              Force re-import
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert data-testid={`${meta.testid}-success`}>
          <AlertTitle>Ingested {rows?.toLocaleString?.() ?? rows ?? 0} rows</AlertTitle>
          <AlertDescription className="text-xs space-y-1">
            <div>
              SHA-256: <code className="font-mono">{result.source_sha256?.slice(0, 12)}…</code>
            </div>
            {meta.key === "stock_balance" && (
              <>
                <div>
                  Items: <b>{result.item_count}</b> · Locations: <b>{result.location_count}</b>
                </div>
                <div>
                  Negative qty cells: <b>{result.negative_qty_count}</b> · Junk columns dropped:{" "}
                  <b>{result.junk_columns_dropped?.length ?? 0}</b>
                </div>
              </>
            )}
            {meta.key === "vendor_rates" && (
              <>
                <div>
                  Items: <b>{result.items_upserted}</b> · Vendors: <b>{result.vendors_upserted}</b> ·
                  Rates: <b>{result.rates_upserted}</b>
                </div>
                {result.parse_warnings?.skus_generated > 0 && (
                  <div className="text-amber-800">
                    {result.parse_warnings.skus_generated} placeholder SKU(s) created for unmatched item names.
                    Sample:{" "}
                    <span className="font-mono">
                      {(result.parse_warnings.unmatched_names || []).slice(0, 3).join(" · ")}
                    </span>
                  </div>
                )}
              </>
            )}
            {meta.key === "sales" && (
              <>
                <div>
                  Range: {result.date_range_start?.slice(0, 10)} → {result.date_range_end?.slice(0, 10)}
                </div>
                <div>
                  Distinct items: <b>{result.distinct_menu_items}</b> · Branches:{" "}
                  <b>{result.distinct_branch_names}</b>
                  {result.unmapped_branch_count > 0 && (
                    <span className="text-amber-800">
                      {" "}· {result.unmapped_branch_count} unmapped branch(es)
                    </span>
                  )}
                </div>
              </>
            )}
            {meta.key === "bom" && (
              <>
                <div data-testid="bom-upload-result-summary">
                  Rows parsed: <b>{result.total_rows_parsed}</b> · Distinct POS SKUs:{" "}
                  <b>{result.distinct_pos_skus}</b> · Ingredients:{" "}
                  <b>{result.total_ingredients}</b>
                </div>
                <div>
                  BOMs created: <b>{result.boms_created}</b> · updated:{" "}
                  <b>{result.boms_updated}</b>
                </div>
                {result.warnings?.unknown_stock_skus?.length > 0 && (
                  <div className="mt-1 text-amber-800" data-testid="bom-upload-warn-unknown">
                    {result.warnings.unknown_stock_skus.length} unknown stock SKU(s):{" "}
                    <span className="italic">
                      {result.warnings.unknown_stock_skus.slice(0, 3)
                        .map((w) => `${w.stock_sku} (${w.stock_name_in_bom})`)
                        .join(" · ")}
                      {result.warnings.unknown_stock_skus.length > 3
                        ? ` · … +${result.warnings.unknown_stock_skus.length - 3} more`
                        : ""}
                    </span>
                  </div>
                )}
                {result.warnings?.uom_mismatches?.length > 0 && (
                  <div className="mt-1 text-amber-800" data-testid="bom-upload-warn-uom">
                    {result.warnings.uom_mismatches.length} UOM mismatch(es) between stock master
                    and BOM ingredients.
                  </div>
                )}
                <div className="mt-1 text-[11px] text-muted-foreground italic">
                  Cascade recompute (calculated levels + per-location activity) running in the
                  background — refresh the dashboard in ~10 s to see updated tiles.
                </div>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function DropZone({ meta }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const doUpload = useCallback(
    async (target, force = false) => {
      if (!target) return;
      setBusy(true);
      setProgress(0);
      setResult(null);
      setError(null);
      try {
        const formData = new FormData();
        formData.append("file", target);
        const url = force ? `${meta.endpoint}?force=true` : meta.endpoint;
        const res = await api.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setProgress(Math.round((evt.loaded / evt.total) * 100));
            }
          },
          timeout: 5 * 60 * 1000,
        });
        setResult(res.data);
        if (res.data.duplicate_upload) {
          toast.warning(`${meta.label}: duplicate detected`);
        } else {
          toast.success(`${meta.label}: ingested`);
        }
      } catch (e) {
        const msg = detailFromError(e);
        setError(msg);
        toast.error(`${meta.label} failed: ${msg}`);
      } finally {
        setBusy(false);
      }
    },
    [meta],
  );

  const onChoose = (evt) => {
    const f = evt.target.files?.[0] || null;
    setFile(f);
    setResult(null);
    setError(null);
    if (f) doUpload(f);
  };

  return (
    <Card className="border-border/70" data-testid={meta.testid}>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">{meta.label}</CardTitle>
            <CardDescription className="text-xs max-w-xl">{meta.description}</CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
            {meta.accept}
          </Badge>
        </div>
        {meta.note && (
          <div
            className="mt-2 text-[11px] text-muted-foreground italic"
            data-testid={`${meta.testid}-note`}
          >
            {meta.note}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <label
          className={
            "block cursor-pointer rounded-md border border-dashed border-border/80 " +
            "px-6 py-8 text-center transition-colors " +
            (busy
              ? "bg-muted/40 cursor-wait"
              : "hover:border-foreground/40 hover:bg-muted/30")
          }
          data-testid={`${meta.testid}-input-label`}
        >
          <input
            type="file"
            accept={meta.accept}
            className="hidden"
            onChange={onChoose}
            disabled={busy}
            data-testid={`${meta.testid}-input`}
          />
          {file ? (
            <div className="space-y-0.5">
              <div className="text-sm font-medium">{file.name}</div>
              <div className="text-xs text-muted-foreground">{bytesLabel(file.size)}</div>
            </div>
          ) : (
            <div className="space-y-0.5">
              <div className="text-sm text-foreground">Click to choose a {meta.accept} file</div>
              <div className="text-xs text-muted-foreground">
                Or drag &amp; drop — upload starts immediately
              </div>
            </div>
          )}
        </label>

        {busy && (
          <div className="space-y-1" data-testid={`${meta.testid}-progress`}>
            <Progress value={progress} />
            <div className="text-xs text-muted-foreground text-right">
              {progress}% uploaded
            </div>
          </div>
        )}

        {error && !busy && (
          <Alert variant="destructive" data-testid={`${meta.testid}-error`}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <ResultBlock result={result} meta={meta} onForce={() => doUpload(file, true)} />
      </CardContent>
    </Card>
  );
}

export default function FnbPoUploads() {
  const [scopeBreakdown, setScopeBreakdown] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/fnb/master-data/status").then((res) => {
      if (!cancelled) {
        setScopeBreakdown(res.data?.stock_balance?.item_scope_breakdown || null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="fnb-po-uploads">
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="fnb-uploads-heading"
          >
            Master Data — Uploads
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Refresh vendor rates, sales history, and (occasionally) bulk-restate the stock balance.
            Daily stock uploads happen on the dashboard.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" data-testid="fnb-uploads-back">
          <Link to="/tools/fnb-po">← Dashboard</Link>
        </Button>
      </header>

      {scopeBreakdown && (
        <div
          className="rounded-lg border border-border/60 bg-background/40 px-4 py-3 text-sm flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="master-data-scope-breakdown"
        >
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            Active item catalogue
          </span>
          <span data-testid="scope-daily">
            <span className="text-lg font-semibold text-emerald-700">
              {scopeBreakdown.daily.toLocaleString()}
            </span>{" "}
            <span className="text-xs text-muted-foreground">daily</span>
          </span>
          <span data-testid="scope-bar">
            <span className="text-lg font-semibold text-amber-800">
              {scopeBreakdown.bar.toLocaleString()}
            </span>{" "}
            <span className="text-xs text-muted-foreground">bar</span>
          </span>
          <span
            data-testid="scope-excluded"
            title="Items with Sub-Category = 'Local items' or blank/null. Not tracked in reorder analytics."
            className="cursor-help border-b border-dotted border-muted-foreground/50"
          >
            <span className="text-lg font-semibold text-rose-700">
              {scopeBreakdown.excluded.toLocaleString()}
            </span>{" "}
            <span className="text-xs text-muted-foreground">excluded</span>
            <span className="text-[10px] text-muted-foreground ml-1">(?)</span>
          </span>
          <span className="text-xs text-muted-foreground ml-auto">
            Total: <b>{scopeBreakdown.total.toLocaleString()}</b>
          </span>
        </div>
      )}

      <div className="space-y-4">
        {ZONES.map((z) => (
          z.linkTo ? (
            <Card
              key={z.key}
              className="border-border/70"
              data-testid={z.testid}
            >
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-base">{z.label}</CardTitle>
                    <CardDescription className="text-xs max-w-xl">
                      {z.description}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                    {z.accept}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Button
                  asChild
                  variant="secondary"
                  size="sm"
                  data-testid={`${z.testid}-open`}
                >
                  <Link to={z.linkTo}>Open {z.label} →</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <DropZone key={z.key} meta={z} />
          )
        ))}
      </div>
    </div>
  );
}
